import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import PromotionalBannersList from "../../features/managePromotionalBanners/bannerList";
import PromotionalBannersTopbar from "../../features/managePromotionalBanners/topbar";
import { useEffect, useState, useCallback } from "react";
import {
  useCreatePromotionalBannerMutation,
  useUpdatePromotionalBannerMutation,
  useLazyDeletePromotionalBannerQuery,
  useLazyGetAllPromotionalBannersQuery,
} from "../../services/promotionalBanners";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import PromotionalBannerDetails from "../../Modals/promotionalBannerDetails";
import usePermission from "../../hooks/usePermission";
import useTranslation from "../../hooks/traslation";
import type {
  PageChangeHandler,
  PromotionalBannerTableRow,
} from "../../types/api";
import { getErrorMessage } from "../../types/api";
import { SUPPORTED_LANGUAGES } from "../../constants/languages";
import type { PromotionalBannerTexts } from "../../services/promotionalBanners";

const ManagePromotionalBanners = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createPromotionalBanner] = useCreatePromotionalBannerMutation();
  const [updatePromotionalBannerMutation] = useUpdatePromotionalBannerMutation();
  const [deletePromotionalBanner] = useLazyDeletePromotionalBannerQuery();
  const [selectedId, setSelectedId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const [getAllPromotionalBanners] = useLazyGetAllPromotionalBannersQuery();

  const [category, setCategory] = useState<string>("");
  const [categoryDisplay, setCategoryDisplay] = useState<string>("");
  const [texts, setTexts] = useState<PromotionalBannerTexts>(() =>
    SUPPORTED_LANGUAGES.reduce<PromotionalBannerTexts>((acc, { code }) => {
      acc[code] = "";
      return acc;
    }, {})
  );
  const [status, setStatus] = useState<boolean>(true);
  const [isPrimary, setIsPrimary] = useState<boolean>(false);


  const [rows, setRows] = useState<PromotionalBannerTableRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isListLoading, setIsListLoading] = useState(true);

  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 10;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const translate = useTranslation();

  const handlePageChange: PageChangeHandler = (_event, newPage) => {
    setPage(Number(newPage) || 1);
  };

  const fetchPromotionalBanners = useCallback(async () => {
    setIsListLoading(true);
    try {
      const response = await getAllPromotionalBanners({
        page,
        limit: itemsPerPage,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        const rawList =
          response?.data?.list ?? response?.data?.data ?? [];
        const list = Array.isArray(rawList)
          ? rawList.map((item: { _id: string }) => ({
              ...item,
              id: item?._id,
            }))
          : [];
        setRows(list);
        setTotalCount(response?.data?.totalCount ?? 0);
      }
    } catch (error) {
      showError(
        getErrorMessage(error, "Failed to load promotional banners")
      );
    } finally {
      setIsListLoading(false);
    }
  }, [page, debouncedSearchTerm, getAllPromotionalBanners, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchPromotionalBanners();
  }, [page, debouncedSearchTerm, fetchPromotionalBanners]);

  const handleSubmit = async () => {
    setIsFormLoading(true);
    const body = { category, texts, status, isPrimary };

    if (selectedId) {
      try {
        const response = await updatePromotionalBannerMutation({
          body,
          id: selectedId,
        }).unwrap();
        if (response?.statusCode === 200) {
          showToast(
            translate?.promotionalBanner?.bannerUpdatedSuccess ||
              "Banner updated successfully"
          );
          setOpen(false);
          resetForm();
          fetchPromotionalBanners();
        }
      } catch (error) {
        showError(
          getErrorMessage(error, translate?.promotionalBanner?.failToUpdate || "Failed to update promotional banner")
        );
      } finally {
        setIsFormLoading(false);
      }
    } else {
      try {
        const response = await createPromotionalBanner({ data: body }).unwrap();
        if (response?.statusCode === 200) {
          showToast(
            translate?.promotionalBanner?.bannerCreatedSuccess ||
              "Promotional banner created successfully"
          );
          setOpen(false);
          resetForm();
          fetchPromotionalBanners();
        }
      } catch (error: unknown) {
        showError(
          getErrorMessage(error, "Failed to create promotional banner")
        );
      } finally {
        setIsFormLoading(false);
      }
    }
  };

  const resetForm = () => {
    setCategory("");
    setCategoryDisplay("");
    setTexts(
      SUPPORTED_LANGUAGES.reduce<PromotionalBannerTexts>((acc, { code }) => {
        acc[code] = "";
        return acc;
      }, {})
    );
    setStatus(true);
    setIsPrimary(false);
    setSelectedId("");
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deletePromotionalBanner({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(
          translate?.promotionalBanner?.bannerDeletedSuccess ||
            "Promotional banner deleted successfully"
        );
        setOpenDelete(false);
        setSelectedId("");
        fetchPromotionalBanners();
      }
    } catch (error) {
      showError(
        getErrorMessage(error, translate?.promotionalBanner?.failToDelete || "Failed to delete promotional banner")
      );
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    resetForm();
  };

  const handleCloseDetailsModal = () => {
    setOpenDetails(false);
    setCategoryDisplay("");
    setSelectedId("");
  };

  const getCategoryId = (row: PromotionalBannerTableRow) =>
    row?.category && typeof row.category === "object"
      ? row.category._id
      : (row?.category as string) ?? "";

  const handleEditClick = (row: PromotionalBannerTableRow) => {
    setSelectedId(row._id);
    setCategory(getCategoryId(row));
    setTexts(
      SUPPORTED_LANGUAGES.reduce<PromotionalBannerTexts>((acc, { code }) => {
        acc[code] = row.texts?.[code] ?? "";
        return acc;
      }, {})
    );
    setStatus(!!row.status);
    setIsPrimary(!!row.isPrimary);
    setOpen(true);
  };

  return (
    <MainContainer isLoading={isListLoading}>
      <div className="main_title">
        <h1>
          {translate?.promotionalBanner?.manageBanners || "Manage Promotional Banners"}
        </h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate?.dashboard?.dashboard || "Dashboard"}
          </span>{" "}
          {translate?.promotionalBanner?.manageBanners || "Manage Promotional Banners"}
        </p>
      </div>

      <div className="cards">
        <PromotionalBannersTopbar
          category={category}
          texts={texts}
          status={status}
          isPrimary={isPrimary}
          setCategory={setCategory}
          setTexts={setTexts}
          setStatus={setStatus}
          setIsPrimary={setIsPrimary}
          handleSubmit={handleSubmit}
          setOpen={setOpen}
          open={open}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          setSearchTerm={setSearchTerm}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          searchTerm={searchTerm}
          isFormLoading={isFormLoading}
        />
        <PromotionalBannersList
          itemsPerPage={itemsPerPage}
          setSelectedId={setSelectedId}
          setOpen={setOpen}
          open={open}
          category={category}
          categoryDisplay={categoryDisplay}
          texts={texts}
          status={status}
          isPrimary={isPrimary}
          setCategory={setCategory}
          setCategoryDisplay={setCategoryDisplay}
          setTexts={setTexts}
          setStatus={setStatus}
          setIsPrimary={setIsPrimary}
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          rows={rows}
          setRows={setRows}
          openDetails={openDetails}
          setOpenDetails={setOpenDetails}
          isLoading={isLoading}
          handleEditClick={handleEditClick}
          handlePageChange={handlePageChange}
          setPage={setPage}
          page={page}
          totalCount={totalCount}
        />
      </div>
      <ConfirmationModal
        text={
          translate?.promotionalBanner?.confirmDelete ||
          "Are you sure you want to delete this promotional banner?"
        }
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
        isLoading={isLoading}
      />
      <PromotionalBannerDetails
        open={openDetails}
        categoryDisplay={categoryDisplay || ""}
        texts={texts}
        status={status}
        isPrimary={isPrimary}
        onClose={handleCloseDetailsModal}
        selectedId={selectedId}
      />
    </MainContainer>
  );
};

export default ManagePromotionalBanners;
